import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Registry } from '@/registry.service';
import { LoggerService } from '@services/logger.service';
import { MsDiscountModel } from '@/models/tables/ms-discount-model';
import { BiggerDiscountService } from '@services/bigger-discount.service';
import { DynamicFormComponent } from '@components/dynamic-form/dynamic-form.component';
import { UserService } from '@services/admin/user.service';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { MSDiscountFormSetting } from '@/form-settings/msdiscount-form-setting';
import { OptionSelect } from '@/models/option-select.model';
import { DynamicFormPopUpInterface } from '@components/dynamic-form/dynamic-form-pop-up-interface';
import { BrandGoalsService } from '@services/data-source-tables/brand-goals.service';
import { EsclusioneGrandiAcquirentiModel } from '@/models/tables/ms-discount-model';
import moment from 'moment';

@Component({
  selector: 'app-ms-discount-form',
  templateUrl: '../../../../../components/dynamic-form/dynamic-form.component.html',
  styleUrls: ['../../../../../components/dynamic-form/dynamic-form.component.scss'],
})
export class MSUpdatesFormComponent
  extends DynamicFormComponent
  implements OnInit, OnChanges, DynamicFormPopUpInterface
{
  @Input() data: MsDiscountModel;
  @Input() modalTrigger$ = new Subject<any>();
  private brands: OptionSelect[] = [];
  private bigBuyers: OptionSelect[] = [];
  selectedBigBuyers: EsclusioneGrandiAcquirentiModel[] = [];

  constructor(
    protected userService: UserService,
    protected toastr: ToastrService,
    protected biggerDiscountService: BiggerDiscountService,
    protected brandGoalsService: BrandGoalsService,
  ) {
    super(toastr, userService);

    this.submitButtonVisible = true;
    this.primaryButton = 'Salva';
    this.secondaryBottomButton = 'Chiudi';

    this.biggerDiscountService.getAllBrands().subscribe((brands) => {
      this.brands = brands.map((brand) => ({
        key: brand,
        value: brand.codiceMarca,
      }));
      this.refreshForm();
    });
  }

  public onSubmit() {
    let msDiscountModel = <any>this.form.getRawValue();
    msDiscountModel.anno = msDiscountModel.anno.year();
    msDiscountModel.mese = msDiscountModel.mese.month() + 1;

    this.biggerDiscountService.saveBiggerDiscount(msDiscountModel).subscribe({
      next: (response) => {
        this.toastr.success('Maggiore Sconto correttamente!');

        this.modalTrigger$.next(1);
      },
      error: (error) => {
        LoggerService.error('saveBiggerDiscount failed!');
        LoggerService.error(error);

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
        }, 1000);
      },
      complete: () => {
        LoggerService.log('Request done');

        setTimeout(() => {
          Registry.getInstance().set('saveInProgress', false);
        }, 1000);
      },
    });
  }

  ngOnInit(): void {
    if (this.data?.marca?.id) {
      this.brandGoalsService.getBigBuyers(this.data.marca.id).subscribe((bigBuyers) => {
        this.bigBuyers = bigBuyers.map((bigBuyer) => ({
          key: bigBuyer,
          value: bigBuyer.codiceGA + ' - ' + bigBuyer.descrizione,
        }));
        this.setupMeta();
        setTimeout(() => {
          this.updateSelectedBigBuyers();
        }, 200);
      });
    } else {
      this.setupMeta();
    }
  }

  ngOnChanges() {
    super.ngOnChanges();
    setTimeout(() => {
      this.updateSelectedBigBuyers();
      this.updateAnnoMese();
    }, 100);
  }

  refreshForm() {
    super.refreshForm();
    if (this.data?.esclusioneGrandiAcquirenti && this.bigBuyers.length > 0) {
      setTimeout(() => {
        this.updateSelectedBigBuyers();
      }, 100);
    }
    setTimeout(() => {
      this.updateAnnoMese();
    }, 100);
  }

  private updateAnnoMese() {
    if (!this.form || !this.data) {
      return;
    }

    // Convert anno (number) to moment object for yearDate picker
    if (this.data.anno && typeof this.data.anno === 'number') {
      const annoControl = this.form.get('anno');
      if (annoControl) {
        annoControl.setValue(moment({ year: this.data.anno }), { emitEvent: false });
      }
    }

    // Convert mese (number, 1-12) to moment object for monthDate picker
    if (this.data.mese && typeof this.data.mese === 'number') {
      const meseControl = this.form.get('mese');
      if (meseControl) {
        // mese is 1-12, but moment expects 0-11, so we subtract 1
        meseControl.setValue(moment({ month: this.data.mese - 1 }), { emitEvent: false });
      }
    }
  }

  private updateSelectedBigBuyers() {
    if (
      !this.form ||
      !this.data?.esclusioneGrandiAcquirenti ||
      !Array.isArray(this.data.esclusioneGrandiAcquirenti)
    ) {
      return;
    }

    if (this.bigBuyers.length === 0) {
      return;
    }

    const esclusioneGrandiAcquirentiControl = this.form.get('esclusioneGrandiAcquirenti');
    if (!esclusioneGrandiAcquirentiControl) {
      return;
    }

    const selectedValues = this.data.esclusioneGrandiAcquirenti
      .map((existing: EsclusioneGrandiAcquirentiModel) => {
        const matchingOption = this.bigBuyers.find((option) => {
          if (!option || !option.key) {
            return false;
          }
          const optionId = option.key.id;
          const existingId = existing.id;
          return optionId === existingId || String(optionId) === String(existingId);
        });
        return matchingOption ? matchingOption.key : null;
      })
      .filter((value) => value !== null);

    if (selectedValues.length > 0) {
      esclusioneGrandiAcquirentiControl.setValue(selectedValues, { emitEvent: false });
      this.selectedBigBuyers = selectedValues;
    }
  }

  onGrandiAcquirentiChange(selectedValues: EsclusioneGrandiAcquirentiModel[]) {
    this.selectedBigBuyers = selectedValues || [];
  }

  bottomButtonAction() {
    this.modalTrigger$.next(1);
  }

  setupMeta(): void {
    this.meta = MSDiscountFormSetting.setClassToCall('MSUpdatesFormComponent')
      .set('brands', this.brands)
      .set('bigBuyers', this.bigBuyers)
      .set('defaultType', this.data.tipo ?? '')
      .getMeta();

    Registry.getInstance().set('MSUpdatesFormComponent', this);
  }
}
